// ChangeEmailModal.jsx
import {
  changeEmailId,
  checkDeletedUserEmail,
  sendVerificationCode,
  validateVerificationCode,
} from "@/api/myProfile";
import { trackApiCall, trackButtonClick } from "@/utils/analytics";
import { maskEmail } from "@/utils/emailMasking";
import { validateForm } from "@/utils/formValidator";
import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";

const EmailChangeModal = ({
  show,
  handleClose,
  onSubmit,
  loading = false,
  emailVerification,
  emailVerificationError,
  setEmailVerification,
  refreshRequired,
}) => {
  const [formData, setFormData] = useState({
    newEmail: "",
    confirmEmail: "",
  });
  const [errors, setErrors] = useState({});
  const [otp, setOtp] = useState(["", "", "", ""]);
  const otpInputRefs = useRef([]);
  const EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const [verificationError, setVerificationError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBlurChange = async (e) => {
    try {
      const { name, value } = e.target;
      const isValid = validateField(name, value);
      if (!isValid) return;

      if (value?.length > 0) {
        const encodedEmail = encodeURIComponent(value);
        const queryString = `emailId=${encodedEmail}`;
        const deletedUserCheck = await checkDeletedUserEmail(queryString);
        if (
          deletedUserCheck?.data?.status === "SUCCESS" &&
          deletedUserCheck?.data?.data === true &&
          deletedUserCheck?.data?.successMessage === "EXPIRED_EMAIL_ID"
        ) {
          setErrors({
            ...errors,
            [name]:
              "The account associated with the given email address is marked for deletion. Please provide a different email address.",
          });
        }
      }
    } catch (error) {
      console.log(error, "error");
    }
  };

  const validateField = (fieldName, value) => {
    let newErrors = { ...errors };

    if (!value || value.trim() === "") {
      newErrors[fieldName] = "This field is required.";
    } else if (!EMAIL_REGEX.test(value.trim())) {
      newErrors[fieldName] = "Please enter a valid email address";
    } else {
      delete newErrors[fieldName];
    }

    // Additional validation for confirmEmail
    if (
      fieldName === "confirmEmail" &&
      value &&
      formData.newEmail &&
      value !== formData.newEmail
    ) {
      newErrors[fieldName] = "Emails do not match";
    }

    setErrors(newErrors);
    return !newErrors[fieldName];
  };

  // useEffect(() => {
  //   if (emailVerificationError?.length > 0) {
  //     setErrors({
  //       newEmail:
  //         "You've exceeded number of attempts, please try again after some time.",
  //       confirmEmail:
  //         "You've exceeded number of attempts, please try again after some time.",
  //     });
  //   }
  // }, [emailVerificationError]);

  useEffect(() => {
    setVerificationError(emailVerificationError || "");
  }, [emailVerificationError]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    const newEmail = formData.newEmail;
    const confirmEmail = formData.confirmEmail;

    const validationErrors = validateForm(
      { newEmail, confirmEmail },
      ["newEmail", "confirmEmail"],
      false,
      false
    );

    if (Object.keys(validationErrors).length > 0) {
      if (validationErrors["newEmail"]) {
        setErrors((prev) => ({
          ...prev,
          newEmail: validationErrors.newEmail,
        }));
      }
      if (validationErrors["confirmEmail"]) {
        setErrors((prev) => ({
          ...prev,
          confirmEmail: validationErrors.confirmEmail,
        }));
      }
      return;
    }

    // check for match
    if (newEmail !== confirmEmail) {
      setErrors((prev) => ({
        ...prev,
        confirmEmail: "Emails do not match",
      }));
      return;
    }
    onSubmit(formData);
  };

  const handleChange = (e, index) => {
    const { value } = e.target;
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < otpInputRefs.current.length - 1) {
        otpInputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index] === "" && index > 0) {
        otpInputRefs.current[index - 1].focus();
      }
    }
  };

  const handleVerifyOTP = async () => {
    trackButtonClick({
      eventName: "confirm_email_validation_pin_click",
      eventCategory: "My Profile",
    });

    try {
      setIsLoading(true);
      setVerificationError("");
      const otpNumber = otp.join("");
      const payload = {
        verificationCodeSentTo: formData.newEmail,
        verificationType: "EMAIL",
        verificationCode: otpNumber,
      };
      const verificationResponse = await validateVerificationCode(payload);

      // Clear OTP after verification attempt
      setOtp(["", "", "", ""]);
      setVerificationError("");

      // Check verification status
      if (
        verificationResponse?.status === "SUCCESS" &&
        verificationResponse?.data?.verificationStatus === "approved"
      ) {
        trackApiCall({
          eventName: "email_validation_successful",
          eventCategory: "My Profile",
        });

        let emailChangeResponse = await changeEmailId({
          newEmail: formData.newEmail,
        });

        if (emailChangeResponse?.data?.status === "SUCCESS") {
          trackApiCall({
            eventName: "change_email_success",
            eventCategory: "My Profile My Shopper",
          });

          toast.success("Email changed successfully");
          refreshRequired(true);
          handleModalClose();
          return;
        }

        // Handle email change failures
        if (emailChangeResponse?.data?.status === "FAILURE") {
          trackApiCall({
            eventName: "confirm_email_failure_error",
            eventCategory: "My Profile",
            eventLabel: `${emailChangeResponse.data.errorMessage}`,
          });

          switch (emailChangeResponse.data.errorMessage) {
            case "EMAIL_ALREADY_ASSOCIATED_TO_LOGGEDIN_USER":
              setVerificationError(
                "This email is already associated with your account."
              );
              break;

            case "EMAIL_ALREADY_EXISTS":
              setVerificationError(
                "This email is already registered with another account."
              );
              break;

            case "INVALID_EMAIL_FORMAT":
              setVerificationError("Please enter a valid email address.");
              break;

            default:
              setVerificationError(
                "Unable to validate email. Please try again."
              );
              break;
          }
          return;
        }

        toast.error("Failed to change email");
        return;
      }

      // Handle verification failures
      if (verificationResponse?.data?.verificationStatus === "denied") {
        setVerificationError("Please enter valid verification code.");
      } else if (verificationResponse?.data?.verificationStatus === "pending") {
        setVerificationError("Verification is still in progress.");
      } else {
        setVerificationError("Verification failed. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred. Please try again.");
      setOtp(["", "", "", ""]);
      setVerificationError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateNewCode = async () => {
    trackButtonClick({
      eventName: "resend_email_validation_pin_click",
      eventCategory: "My Profile",
      eventSource: "on resend otp click",
    });
    try {
      setIsLoading(true);
      setVerificationError("");
      const verificationCodeResponse = await sendVerificationCode({
        verificationCodeSentTo: formData?.newEmail,
        verificationType: "EMAIL",
      });
      if (verificationCodeResponse?.data?.status === "SUCCESS") {
        setEmailVerification(true);
        setOtp(["", "", "", ""]);
      } else {
        if (
          verificationCodeResponse?.data?.errorCode ===
          "VERIFICATION_CODE_LIMIT_EXEEDED"
        ) {
          setVerificationError(
            "You've exceeded number of attempts, please try again after some time."
          );
        }
      }
    } catch (error) {
      console.log(
        "🚀 ~ ChangeEmailPopup.jsx:197 ~ handleGenerateNewCode ~ error:",
        error
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    trackButtonClick({
      eventName: "close_email_validation_popup_click",
      eventCategory: "My Profile My Shopper",
    });

    setErrors({});
    setFormData({
      confirmEmail: "",
      newEmail: "",
    });
    handleClose();
    setEmailVerification(false);
    setVerificationError("");
  };

  return (
    <Modal show={show} onHide={handleModalClose} centered>
      <Modal.Header closeButton style={{ borderBottom: 0 }}>
        <Modal.Title className="p-2 change-email-modal-title">
          Change Email
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="mx-4">
        {emailVerification ? (
          <>
            <p>
              Enter the verification code sent to :{" "}
              <span className="change-email-span">
                {maskEmail(formData.newEmail)}
              </span>{" "}
            </p>

            <div>
              <div className="otp-form">
                <h4 className="otp-header mb-3">Verify Your Email ID</h4>
                <div className="otp-input-row d-flex align-items-center">
                  <div className="otp-container">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        type="text"
                        value={digit}
                        onChange={(e) => handleChange(e, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        maxLength="1"
                        className="otp-input"
                        ref={(el) => (otpInputRefs.current[index] = el)}
                      />
                    ))}
                  </div>

                  <Button
                    className="hubzu-btn-thm w-25 ms-3 fs-12"
                    onClick={handleVerifyOTP}
                    disabled={otp.join("").length !== 4}
                  >
                    Verify
                  </Button>
                </div>
              </div>
              {verificationError?.length > 0 && (
                <p className="text-danger mt-2">{verificationError}</p>
              )}

              {isLoading && (
                <div className="loading-animation-container mt-3">
                  <div className="loading-dots">
                    <div className="loader-dot"></div>
                    <div className="loader-dot"></div>
                    <div className="loader-dot"></div>
                  </div>
                  {/* <p className="loading-text">Verifying code...</p> */}
                </div>
              )}

              <p className="help-text">
                Didn't get the verification code?{" "}
                <span
                  className="verification-link next-navlink"
                  onClick={handleGenerateNewCode}
                >
                  Click here
                </span>{" "}
                to generate a new one.
              </p>
            </div>
          </>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              {/* <Form.Label>
                New Email<span className="text-danger">*</span>
              </Form.Label> */}
              <Form.Control
                type="email"
                name="newEmail"
                value={formData.newEmail}
                onChange={handleInputChange}
                isInvalid={!!errors?.newEmail}
                onBlur={handleBlurChange}
                placeholder="New Email"
              />
              <Form.Control.Feedback type="invalid">
                {errors?.newEmail}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              {/* <Form.Label>
                Confirm Email<span className="text-danger">*</span>
              </Form.Label> */}
              <Form.Control
                type="email"
                name="confirmEmail"
                value={formData.confirmEmail}
                onChange={handleInputChange}
                isInvalid={!!errors?.confirmEmail}
                onBlur={handleBlurChange}
                placeholder="Confirm Email"
              />
              <Form.Control.Feedback type="invalid">
                {errors?.confirmEmail}
              </Form.Control.Feedback>
            </Form.Group>

            {verificationError != null && verificationError?.length > 0 && (
              <p className="text-danger mt-2" style={{ fontSize: "11px" }}>
                {verificationError}
              </p>
            )}

            <div className="d-flex justify-content-center gap-2 pb-4">
              <Button
                variant="primary"
                type="submit"
                disabled={loading}
                className="w-100 hubzu-btn-thm mt-3"
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Updating...
                  </>
                ) : (
                  "Update"
                )}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default EmailChangeModal;
